import { useMemo, useRef, useState } from 'react';
import type { Category, Product, ProductCondition, ProductStatus } from '../types';
import { compressImage, formatBytes } from '../utils/image';
import { conditionLabels, statusLabels } from '../utils/labels';

type NewImage = { file: File; url: string };

export default function ProductEditor({ product, categories, onClose, onSaved }: {
  product: Product | null;
  categories: Category[];
  onClose: () => void;
  onSaved: (id: string | null, form: FormData) => Promise<void>;
}) {
  const [title, setTitle] = useState(product?.title || '');
  const [description, setDescription] = useState(product?.description || '');
  const [condition, setCondition] = useState<ProductCondition>(product?.condition || 'like_new');
  const [categoryId, setCategoryId] = useState(product?.category_id || categories.find((item) => item.parent_id)?.id || categories[0]?.id || '');
  const [price, setPrice] = useState(product ? String(product.price_cents / 100) : '');
  const [oldPrice, setOldPrice] = useState(product?.old_price_cents ? String(product.old_price_cents / 100) : '');
  const [status, setStatus] = useState<ProductStatus>(product?.status || 'active');
  const [existing, setExisting] = useState(product?.images || []);
  const [newImages, setNewImages] = useState<NewImage[]>([]);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const galleryRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const slotsLeft = 4 - existing.length - newImages.length;
  const totalSize = useMemo(() => newImages.reduce((sum, image) => sum + image.file.size, 0) + existing.reduce((sum, image) => sum + (image.size_bytes || 0), 0), [newImages, existing]);

  const addFiles = async (list: FileList | null) => {
    if (!list?.length) return;
    const files = Array.from(list).slice(0, slotsLeft);
    if (files.length < list.length) setError('ניתן לשמור עד ארבע תמונות למוצר');
    setProcessing(true);
    try {
      const compressed: NewImage[] = [];
      for (const file of files) {
        const result = await compressImage(file);
        compressed.push({ file: result, url: URL.createObjectURL(result) });
      }
      setNewImages((value) => [...value, ...compressed]);
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'עיבוד התמונה נכשל'); }
    finally { setProcessing(false); if (galleryRef.current) galleryRef.current.value = ''; if (cameraRef.current) cameraRef.current.value = ''; }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!existing.length && !newImages.length) return setError('יש להוסיף לפחות תמונה אחת');
    setSaving(true); setError('');
    try {
      const form = new FormData();
      form.append('payload', JSON.stringify({
        title, description, condition, category_id: categoryId,
        price_cents: Math.round(Number(price) * 100),
        old_price_cents: oldPrice ? Math.round(Number(oldPrice) * 100) : null,
        status, keep_image_ids: existing.map((image) => image.id)
      }));
      newImages.forEach((image) => form.append('images', image.file));
      await onSaved(product?.id || null, form);
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'שמירת המוצר נכשלה'); }
    finally { setSaving(false); }
  };

  return <div className="modal-backdrop admin-modal-backdrop"><form className="editor-modal" onSubmit={submit}>
    <button type="button" className="modal-close" onClick={onClose}>×</button>
    <div className="editor-heading"><div><span className="eyebrow">ניהול מוצר</span><h2>{product ? 'עריכת מוצר' : 'הוספת מוצר חדש'}</h2></div><span className="storage-pill">{formatBytes(totalSize)} בתמונות</span></div>
    {error && <div className="form-error">{error}</div>}
    <div className="editor-grid">
      <section className="editor-fields">
        <label>שם המוצר<input required maxLength={100} value={title} onChange={(event) => setTitle(event.target.value)} placeholder="לדוגמה: פאזל נסיכות 42 חלקים" /></label>
        <label>תיאור<textarea rows={5} maxLength={2000} value={description} onChange={(event) => setDescription(event.target.value)} placeholder="פרטים נוספים על המוצר" /></label>
        <div className="two-fields"><label>מצב המוצר<select value={condition} onChange={(event) => setCondition(event.target.value as ProductCondition)}>{Object.entries(conditionLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label><label>קטגוריה<select required value={categoryId} onChange={(event) => setCategoryId(event.target.value)}>{categories.map((item) => <option key={item.id} value={item.id}>{item.parent_id ? `↳ ${item.name}` : item.name}</option>)}</select></label></div>
        <div className="two-fields"><label>מחיר חדש בשקלים<input required min="0" step="0.01" inputMode="decimal" value={price} onChange={(event) => setPrice(event.target.value)} /></label><label>מחיר קודם, אופציונלי<input min="0" step="0.01" inputMode="decimal" value={oldPrice} onChange={(event) => setOldPrice(event.target.value)} /></label></div>
        <label>סטטוס<select value={status} onChange={(event) => setStatus(event.target.value as ProductStatus)}>{Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
      </section>
      <section className="image-editor">
        <h3>תמונות המוצר</h3><p>תמונה ראשונה תשמש כתמונה הראשית. כל תמונה מוקטנת ונדחסת אוטומטית.</p>
        <div className="editor-images">
          {existing.map((image, index) => <div className="editor-image" key={image.id}><img src={image.url} alt="" /><span>{index === 0 ? 'ראשית' : index + 1}</span><button type="button" onClick={() => setExisting((items) => items.filter((item) => item.id !== image.id))}>×</button></div>)}
          {newImages.map((image, index) => <div className="editor-image" key={image.url}><img src={image.url} alt="" /><span>{existing.length + index === 0 ? 'ראשית' : existing.length + index + 1}</span><button type="button" onClick={() => { URL.revokeObjectURL(image.url); setNewImages((items) => items.filter((item) => item.url !== image.url)); }}>×</button></div>)}
          {Array.from({ length: slotsLeft }).map((_, index) => <div className="empty-image-slot" key={index}>＋</div>)}
        </div>
        <div className="upload-actions"><button type="button" className="secondary" disabled={!slotsLeft || processing} onClick={() => galleryRef.current?.click()}>בחירה מהגלריה</button><button type="button" className="secondary" disabled={!slotsLeft || processing} onClick={() => cameraRef.current?.click()}>צילום מהמצלמה</button></div>
        <input ref={galleryRef} hidden type="file" accept="image/*" multiple onChange={(event) => addFiles(event.target.files)} /><input ref={cameraRef} hidden type="file" accept="image/*" capture="environment" onChange={(event) => addFiles(event.target.files)} />
        {processing && <p className="processing-note"><span className="mini-loader" /> מתאים ומכווץ את התמונות...</p>}
      </section>
    </div>
    <div className="editor-actions"><button type="button" className="secondary" onClick={onClose}>ביטול</button><button className="primary" disabled={saving || processing}>{saving ? 'שומר...' : 'שמירת המוצר'}</button></div>
  </form></div>;
}
